import React, { CSSProperties } from 'react';
import classNames from 'classnames/bind';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';

import { ScrollArea } from 'components/ScrollArea/ScrollArea';

import { ContextMenuSeparator } from './ContextMenuSeparator';
import { ContextMenuLabel, ContextMenuLabelProps } from './ContextMenuLabel';
import { ContextMenuAction, ContextMenuActionProps } from './ContextMenuAction';
import { ContextMenuGroup, ContextMenuGroupProps } from './ContextMenuGroup';
import { ContextMenuOption, ContextMenuOptionProps } from './ContextMenuOption';
import {
  ContextMenuOptionGroup,
  ContextMenuOptionGroupProps,
} from './ContextMenuOptionGroup';

import styles from './ContextMenu.module.scss';

const c = classNames.bind(styles);

export type ContextMenuChild =
  | React.ReactElement<
      | ContextMenuLabelProps
      | ContextMenuActionProps
      | ContextMenuOptionProps
      | ContextMenuOptionGroupProps
      | ContextMenuGroupProps
    >
  | undefined
  | null
  | false;

export type ContextMenuProps = {
  align?: RadixDropdownMenu.DropdownMenuContentProps['align'];
  side?: RadixDropdownMenu.DropdownMenuContentProps['side'];
  trigger: React.ReactElement;
  /** Pinned above the scrollable menu content, e.g. a search field. */
  header?: React.ReactNode;
  children: ContextMenuChild | ContextMenuChild[];
  width?: CSSProperties['width'];
  onOpenChange?: (open: boolean) => void;
};

export type ContextMenuStateProps = {
  open: boolean;
  setOpen: (nextOpen: boolean) => void;
};

const ContextMenuState = React.createContext<ContextMenuStateProps | undefined>(
  undefined
);

export function useContextMenuState() {
  const context = React.useContext(ContextMenuState);

  if (context === undefined) {
    throw new Error(
      'Hook `useContextMenuState` can only be used within a `ContextMenu` component.'
    );
  }

  return context;
}

const MAX_HEIGHT_REM = 25;

// Keys a header input passes through to the menu (list navigation and
// dismissal); everything else stays local so the menu's typeahead does
// not steal focus while typing.
const HEADER_PASSTHROUGH_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];

function handleHeaderKeyDown(event: React.KeyboardEvent) {
  if (!HEADER_PASSTHROUGH_KEYS.includes(event.key)) {
    event.stopPropagation();
  }
}

function pxToRem(px: number) {
  const bodyFontSize = getComputedStyle(document.documentElement).fontSize;
  return px / parseFloat(bodyFontSize);
}

export function ContextMenu({
  trigger,
  header,
  children,
  align = 'end',
  side,
  width,
  onOpenChange,
  ...rest
}: ContextMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  const resizeMenu = React.useCallback(() => {
    window.setTimeout(() => {
      const el = ref.current;

      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const padding = 8;
      const availableSpace = window.innerHeight - rect.top - padding;
      const availableSpaceRem = pxToRem(availableSpace);

      el.style.setProperty(
        '--max-height',
        `${Math.min(availableSpaceRem, MAX_HEIGHT_REM)}rem`
      );
    });
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resizeMenu();
    }
  }, [isOpen, resizeMenu]);

  React.useEffect(() => {
    let timeout: number;

    function resizeListener() {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(resizeMenu, 400);
    }

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeMenu]);

  function handleCloseAutoFocus(event: Event) {
    event.preventDefault();
  }

  // Radix always focuses the first menu item on open (and DropdownMenu does
  // not expose onOpenAutoFocus), so when a header is present move focus to
  // its first control just after Radix has run its own focus.
  const hasHeader = Boolean(header);
  React.useEffect(() => {
    if (!isOpen || !hasHeader) {
      return;
    }

    const timeout = window.setTimeout(() => {
      ref.current
        ?.querySelector<HTMLElement>(
          'input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        ?.focus();
    });

    return () => window.clearTimeout(timeout);
  }, [isOpen, hasHeader]);

  return (
    <ContextMenuState.Provider
      value={{ open: isOpen, setOpen: handleOpenChange }}
    >
      <RadixDropdownMenu.Root
        {...rest}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>

        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.Content
            className={c('menu')}
            onCloseAutoFocus={handleCloseAutoFocus}
            sideOffset={8}
            align={align}
            side={side}
            style={{ width }}
            loop
            ref={ref}
          >
            <div className={c('frame')}>
              {header && (
                <div className={c('header')} onKeyDown={handleHeaderKeyDown}>
                  {header}
                </div>
              )}
              <ScrollArea className={c('content')}>{children}</ScrollArea>
            </div>
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
    </ContextMenuState.Provider>
  );
}

export {
  ContextMenuGroup,
  ContextMenuSeparator,
  ContextMenuLabel,
  ContextMenuOption,
  ContextMenuOptionGroup,
  ContextMenuAction,
};
